import {
  anchorPosition,
  boundsOfValues,
  formatMeasurement,
  type Evaluated,
} from '@/domain/construction';
import { formulaLabel } from '@/domain/expression';
import { bounds as boundsOps, type Bounds } from '@/domain/geometry';
import { type Vec2 } from '@/domain/math';
import { type EntityId, inDrawOrder, type Style } from '@/domain/model';
import { type Scene } from '@/engine';

import { coveredSegments, cutIntoPieces, withoutLiftedEdges } from '../painters/hiddenLines';
import { nameDirections } from '../painters/namePlacement';
import { labelSize } from '../painters/paintValue';
import { washesFirst } from '../paintOrder';
import { approximateMeasurer, layoutText } from '../text';

/**
 * Widths for framing, without a canvas.
 *
 * `exportFrame` runs before any output context exists — a PDF has no measuring device at all —
 * so the ratio-based estimate is the only one available here. It is the same one the SVG writer
 * uses to lay the glyphs out, so the frame and the letters agree.
 */
const FRAME_MEASURER = approximateMeasurer();

/**
 * What an export draws, and the frame it draws into.
 *
 * Every exporter — raster, SVG, PDF — starts here, so that a PNG, an SVG and a PDF of the
 * same document contain the same objects in the same order. Three separate enumerations
 * would drift, and the drift would show up as "the PDF is missing the labels" long after
 * the change that caused it.
 *
 * This is deliberately *not* the renderer's paint loop. The renderer draws what is on
 * screen, at the camera's zoom, with the overlay and grid on top; an export draws the
 * document, framed on its content, with neither. Sharing the loop would mean one of the two
 * carrying flags for the other's case.
 */

export interface Drawable {
  readonly id: EntityId;
  readonly value: Evaluated;
  readonly style: Style;
  /**
   * The entity's name, drawn beside it.
   *
   * Carried here rather than looked up by each renderer, so a name that appears on screen
   * appears in every export. It used to exist only in the object list, which made "label a
   * point A" a thing the user could do and then not see.
   */
  readonly label?: string;
  /**
   * Which way the name is written, as a unit vector in world space.
   *
   * Computed once for the whole scene rather than per renderer, because it depends on *other*
   * entities — the edges meeting the point — and a renderer that only sees one drawable at a
   * time cannot work it out. Present only when the entity has a name.
   */
  readonly nameDirection?: Vec2;
  /**
   * The solid covers this line, so it is drawn dashed whatever its style says.
   *
   * Not a style, because it is not a choice. The same two points joined in the same order are
   * hidden from one side of a solid and visible from the other, and a figure that let the author
   * decide would be one that could state the wrong thing.
   */
  readonly covered?: boolean;
  /**
   * Which edges of a chain the solid covers, when the answer differs along it.
   *
   * A polygon is not one line with one answer: a section through a tetrahedron has an edge lying
   * in a face the reader can see and another running through the middle of the block. Present
   * only when at least one edge is covered, so the common case costs nothing.
   */
  readonly coveredEdges?: readonly boolean[];
  /** Which of its sides another entity now draws, and this one should therefore not. */
  readonly liftedEdges?: readonly boolean[];
}

/**
 * Which entities an export contains.
 *
 * Separate from {@link FrameOptions} because the two answer different questions — *what* is
 * drawn against *how big the paper is* — and every exporter needs both.
 */
export interface ContentOptions {
  /**
   * Draw only these entities. Omitted means the whole drawing.
   *
   * The frame follows: an export of two objects is cropped to those two objects, because a
   * picture of a selection framed on the whole page would be mostly blank and the thing the
   * user asked for would be a stamp in one corner.
   */
  readonly only?: ReadonlySet<EntityId> | undefined;
}

export interface ExportFrame {
  /** The world rectangle being exported. */
  readonly world: Bounds;
  /** Output size in points or pixels. */
  readonly width: number;
  readonly height: number;
  /** Output units per world unit. */
  readonly scale: number;
}

export interface FrameOptions {
  /** Blank space around the content, in world units. */
  readonly margin?: number;
  /** Output units per world unit. 1 means one pixel per world unit. */
  readonly scale?: number;
  /** Used when the document has no bounded content to frame on. */
  readonly fallback?: Bounds;
  /**
   * Width ÷ height the exported frame is brought to, by adding blank space.
   *
   * ## Why a figure wants a stated shape
   *
   * A drawing framed to its own content comes out whatever shape it happens to be, and a set of
   * them comes out a set of different shapes. Dropped into an exam paper one after another, each
   * needs its own resizing and they end up visibly inconsistent on the page. A teacher building a
   * worksheet wants `4:3` and wants every figure to be `4:3`.
   *
   * ## Why it pads and never stretches
   *
   * Because stretching to a shape is exactly the thing this editor exists not to do. Squeezing a
   * figure into `16:9` would turn a circle into an ellipse and a right angle into something else,
   * and the whole promise is that the picture is true. Padding adds paper, which changes nothing
   * about the drawing.
   *
   * The space is added symmetrically, so the content stays centred rather than drifting to one
   * corner. Absent means the frame keeps the shape of its content, which is the right default:
   * `y = sin x` in a square is a squiggle across the middle with white above and below it, and a
   * user who has not asked for a shape should not be given that one.
   */
  readonly aspect?: number;
}

export const DEFAULT_MARGIN = 24;

/**
 * The frame shapes offered, as width ÷ height.
 *
 * Named for what they are used for. Portrait and landscape are both here because a tall figure
 * padded to a landscape shape is mostly paper — the point of offering a shape is that it fits
 * what is being framed.
 */
export const FRAME_ASPECTS = {
  square: 1,
  landscape43: 4 / 3,
  portrait34: 3 / 4,
  landscape32: 3 / 2,
  portrait23: 2 / 3,
  wide169: 16 / 9,
} as const;

/**
 * The entities an export should draw, in paint order.
 *
 * Hidden entities are omitted, and so are constructions that currently evaluate to nothing —
 * the same two rules the on-screen renderer follows, so that what is exported is what was
 * on the page.
 */
export function drawList(scene: Scene, only?: ReadonlySet<EntityId>): readonly Drawable[] {
  const result: Drawable[] = [];
  /*
   * Both are computed over the **whole** scene even when only part of it is drawn.
   *
   * They are facts about the drawing rather than about the export: which way a name is
   * written depends on the edges around it, and whether a line is dashed depends on the solid
   * in front of it. Recomputing them from the subset would make an exported piece differ from
   * the same piece on screen — a name flipping to the other side, an edge turning solid —
   * which is exactly what a user cropping an image does not expect.
   */
  const directions = nameDirections(scene);
  const covered = coveredSegments(scene);

  for (const entity of inDrawOrder(scene.document)) {
    if (!entity.visible) continue;
    if (only !== undefined && !only.has(entity.id)) continue;
    const value = scene.values.get(entity.id);
    if (value === null || value === undefined) continue;
    const direction = directions.get(entity.id);

    /*
     * One drawable per stretch a solid covers, so a line running out from behind a block is
     * dashed where the block covers it and solid where it does not — in every format, without
     * any exporter learning what a stretch is. A drawable is already *what to draw* rather than
     * what the document says, and a segment covered in part is genuinely two of those.
     *
     * The label goes on the first piece only. It belongs to the entity, not to a fragment of it,
     * and hanging it on each would print the name once per piece.
     */
    const pieces = cutIntoPieces(
      // See the note in `Renderer`: a lifted edge belongs to its segment now, in every format.
      withoutLiftedEdges(value, covered.lifted.get(entity.id) ?? []),
      covered.parts.get(entity.id)
    );

    for (const [index, piece] of pieces.entries()) {
      const named = index === 0;
      result.push({
        id: entity.id,
        value: piece.value,
        style: entity.style,
        ...(entity.label === undefined || !named ? {} : { label: entity.label }),
        ...(direction === undefined || !named ? {} : { nameDirection: direction }),
        ...(piece.covered || covered.whole.has(entity.id) ? { covered: true } : {}),
        ...(covered.edges.has(entity.id)
          ? { coveredEdges: covered.edges.get(entity.id) ?? [] }
          : {}),
        ...(covered.liftedEdges.has(entity.id)
          ? { liftedEdges: covered.liftedEdges.get(entity.id) ?? [] }
          : {}),
      });
    }
  }

  /*
   * Regions to the back, in the order they were written, then everything else. A shaded plane
   * written late in a script is otherwise painted over the vertices it was meant to explain —
   * see `paintOrder`, where the rule and the figure that produced it are set out.
   */
  return washesFirst(unstacked(result), (drawable) => drawable.style);
}

/**
 * Captions that land on the same spot, moved apart.
 *
 * ## Why it cannot be done where the position is computed
 *
 * Because the evaluator sees one entity and its parents, and a collision is a fact about
 * *two*. `text AC "2√3a"` and `text BD "2a"` are each perfectly placed — a caption sits beside
 * the midpoint of the segment it names — and the two diagonals of a rhombus share that midpoint,
 * so both are correct and together they set `2a√3a`. Stepping off the segment perpendicular,
 * which the evaluator does do, separates them by the gap and the gap is a fraction of a word.
 *
 * So this is a scene-level pass, like `nameDirections` beside it and for the same reason: a name
 * is placed by looking at the other ink, and a caption has to be too.
 *
 * ## Why here rather than in each renderer
 *
 * Because a drawable is already *what to draw* rather than what the document says — it carries
 * a name direction the entity does not have and a dashed flag the style does not. Adjusting the
 * position here means the canvas, the SVG, the PDF and the frame calculation all agree without
 * any of them knowing this rule exists. Four copies of a placement rule is how two of them end
 * up disagreeing about where a label went.
 *
 * ## Stacking, rather than something cleverer
 *
 * The captions in a cluster are laid out one under another in the order they were written. It is
 * what a draughtsman does with two dimensions meeting at a point, it is stable — the same script
 * always produces the same figure — and it needs no search. Whichever was written first keeps
 * its place, so adding a caption never moves the ones already there.
 */
function unstacked(drawables: readonly Drawable[]): readonly Drawable[] {
  const captions = drawables.filter(
    (drawable) => drawable.value.kind === 'text' || drawable.value.kind === 'measurement'
  );
  if (captions.length < 2) return drawables;

  const moved = new Map<EntityId, Vec2>();
  const placed: { at: Vec2; step: number }[] = [];

  for (const drawable of captions) {
    const value = drawable.value;
    if (value.kind !== 'text' && value.kind !== 'measurement') continue;
    const step = labelSize(drawable.style) * LINE_STACK;

    /*
     * Nearest free line, below or above, and never more than {@link REACH} of them away.
     *
     * The cap is the part that matters, and it was put there by measuring. Moving down until
     * something is free is the obvious rule and it is wrong: on a rhombus captioned on both
     * diagonals and its height, the third caption clashed at every step and travelled 65 units
     * — out of the figure it belonged to and next to an edge it had nothing to do with. A
     * caption on top of another is hard to read; a caption pointing at the wrong line is a
     * figure telling the reader something false, which is worse. So past the cap it stays
     * where the geometry put it and the overlap remains, visible and honest.
     *
     * Both directions, because one of them is usually free at the first step: the same figure
     * placed that third caption one line *up* with nothing in its way.
     *
     * Measured against where the earlier captions were *put*, not where they asked to be, so
     * three at one point come out as three lines rather than two together and one below.
     */
    const free = (candidate: Vec2): boolean =>
      !placed.some(
        (other) =>
          Math.abs(other.at.x - candidate.x) < CLUSTER * Math.max(step, other.step) &&
          Math.abs(other.at.y - candidate.y) < Math.max(step, other.step) * 0.9
      );

    let at = value.position;
    if (!free(at)) {
      const rungs: number[] = [];
      for (let k = 1; k <= REACH; k += 1) rungs.push(-k * step, k * step);
      const found = rungs
        .map((dy) => ({ x: value.position.x, y: value.position.y + dy }))
        .find((candidate) => free(candidate));
      at = found ?? value.position;
    }

    placed.push({ at, step });
    if (at !== value.position) moved.set(drawable.id, at);
  }

  if (moved.size === 0) return drawables;
  return drawables.map((drawable) => {
    const at = moved.get(drawable.id);
    if (at === undefined) return drawable;
    const value = drawable.value;
    if (value.kind !== 'text' && value.kind !== 'measurement') return drawable;
    return { ...drawable, value: { ...value, position: at } };
  });
}

/** Line spacing for stacked captions, as a multiple of the label size. */
const LINE_STACK = 1.35;

/**
 * How many lines a caption may be moved before the attempt is abandoned.
 *
 * Two, which is far enough to clear a pair and near enough that the caption still reads as
 * belonging to its own line. See the note at the search for what happens past it, and why
 * leaving the overlap is the better of the two failures.
 */
const REACH = 2;

/**
 * How near two captions have to be, horizontally, to count as clashing — in line heights.
 *
 * Wider than it is tall because a caption is: `2√3a` is four characters and one line, so two of
 * them a character apart overlap while two a line apart do not. Judged on the anchors rather
 * than on the measured strings, which would be exact and would also make the layout depend on
 * the font the reader happens to have.
 */
const CLUSTER = 2.4;

/**
 * A selection widened to include what it cannot be read without.
 *
 * Today that means one thing: a graph brings its axes. The reasoning is the same one that
 * makes `addFunctionGraph` create them in the first place — a curve on a blank page says
 * nothing at all about where the origin is or what one unit means, so an exported graph
 * without them is a squiggle. A user who selects a parabola and asks for a picture of it
 * means the picture a parabola comes in.
 *
 * Only ever a **superset**, so nothing the user chose is dropped. Anything else in the
 * drawing — a second curve, a table, a triangle — is left out, which is the whole point.
 */
export function withFrameOfReference(
  scene: Scene,
  ids: ReadonlySet<EntityId>
): ReadonlySet<EntityId> {
  const hasGraph = [...ids].some(
    (id) => scene.document.entities.get(id)?.source.op === 'function-graph'
  );
  if (!hasGraph) return ids;

  const widened = new Set(ids);
  for (const entity of scene.document.entities.values()) {
    if (entity.source.op === 'axes') widened.add(entity.id);
  }
  return widened;
}

/**
 * The frame to export into.
 *
 * Framed on the *bounded* content. Lines and rays have no extent, so including them would
 * be meaningless; they are drawn clipped to whatever frame the bounded objects produce,
 * which is what a reader expects a drawing of a line to look like.
 *
 * The margin is not decoration. Text and measurements report the bounds of their anchor
 * rather than of their glyphs — measuring a glyph needs a canvas and a font, which the
 * domain does not have — so a label near the edge would be cropped by a tight frame.
 */
/**
 * The box the *lettering* occupies, which the geometry's own bounds do not cover.
 *
 * `boundsOfValue` answers for a caption with its anchor point and says so — the glyphs have no
 * extent the domain can know, because measuring them needs a font. The consequence was a frame
 * fitted to the geometry alone, so a caption near the edge ran off it: a figure of a rhombus
 * with `text noteAt "d(O,(SAB)) = a√3/4"` at the right of the drawing exported as `d(O,` and
 * nothing else. Silent, and only visible in the finished worksheet.
 *
 * `layoutText`'s own note says the caller framing an export measures the result rather than
 * assuming it. This is that caller finally doing so.
 *
 * Measured at scale 1 in world units, because that is the space the frame is computed in; the
 * scale is applied to the whole frame afterwards. Generous rather than exact — a label's
 * descenders and the name gap are approximated by a full line height all round — because the
 * failure modes are not symmetric. A frame slightly too large costs white space; a frame
 * slightly too small costs the end of a word.
 */
function lettering(drawables: readonly Drawable[]): Bounds {
  const around = (at: Vec2, content: string, style: Style): readonly Bounds[] => {
    const fontPx = labelSize(style);
    const layout = layoutText(content, { fontPx, measure: FRAME_MEASURER });
    if (layout.lines.length === 0) return [];
    /*
     * Both directions from the anchor. A caption runs right from its position and a name is
     * placed on whichever side of its object was free, so which way the letters go is decided
     * downstream of here — and guessing wrong is exactly the clipping this exists to prevent.
     */
    const wide = layout.width + fontPx;
    const tall = layout.height + fontPx;
    return [boundsOps.bounds(at.x - wide, at.y - tall, at.x + wide, at.y + tall)];
  };

  /**
   * The same, for text whose direction is already settled.
   *
   * `around` allows for the letters running either way because a *name* is placed on whichever
   * side of its object was free, and that is decided downstream. A formula is not: it is drawn
   * left-aligned from its anchor, always. Allowing for it in both directions doubled its
   * contribution, and it is the longest string a figure carries — enough to widen an exported
   * image by more than the drawing itself.
   */
  const rightward = (at: Vec2, content: string, style: Style): readonly Bounds[] => {
    const fontPx = labelSize(style);
    const layout = layoutText(content, { fontPx, measure: FRAME_MEASURER });
    if (layout.lines.length === 0) return [];
    const tall = layout.height;
    return [boundsOps.bounds(at.x, at.y - tall, at.x + layout.width + fontPx, at.y + tall)];
  };

  const boxes = drawables.flatMap((drawable) => {
    const value = drawable.value;
    const named = drawable.label === undefined ? null : anchorPosition(value);
    /*
     * A graph's formula counts too, and used to not.
     *
     * It is the longest piece of text a figure normally carries — `y = 2·x³ + 5·x² + 5·x + 2` is
     * wider than the curve it names — and it was left out of this sum, so it ran off the edge of
     * every export it did not happen to fit inside. What the frame is for is that nothing drawn
     * lands outside it.
     */
    const formula = value.kind === 'function-graph' ? formulaLabel(value.expression) : null;

    return [
      ...(value.kind === 'text' ? around(value.position, value.content, drawable.style) : []),
      ...(value.kind === 'measurement'
        ? around(value.position, formatMeasurement(value.value), drawable.style)
        : []),
      ...(value.kind === 'function-graph' && formula !== null && value.labelAt !== null
        ? rightward(value.labelAt, formula, drawable.style)
        : []),
      ...(named === null || drawable.label === undefined
        ? []
        : around(named, drawable.label, drawable.style)),
    ];
  });

  return boxes.reduce<Bounds>((box, next) => boundsOps.union(box, next), boundsOps.EMPTY);
}

export function exportFrame(
  drawables: readonly Drawable[],
  options: FrameOptions = {}
): ExportFrame {
  const margin = options.margin ?? DEFAULT_MARGIN;
  const scale = options.scale ?? 1;

  const content = boundsOfValues(drawables.map((drawable) => drawable.value));
  const base =
    content === null || boundsOps.isEmpty(content)
      ? (options.fallback ?? boundsOps.bounds(-200, -200, 200, 200))
      : content;

  const framed = boundsOps.expand(boundsOps.union(base, lettering(drawables)), margin);
  const world = options.aspect === undefined ? framed : padTo(framed, options.aspect);

  return {
    world,
    width: Math.max(1, Math.round(boundsOps.width(world) * scale)),
    height: Math.max(1, Math.round(boundsOps.height(world) * scale)),
    scale,
  };
}

/**
 * The same box, opened out on one pair of sides until it has the shape asked for.
 *
 * Never on both pairs, and never inward: whichever dimension is already short for the shape is
 * the one that grows, so the content is untouched and stays centred. Growing both would only
 * scale the paper; shrinking either would crop the drawing.
 */
function padTo(box: Bounds, aspect: number): Bounds {
  const width = boundsOps.width(box);
  const height = boundsOps.height(box);
  if (!(aspect > 0) || !Number.isFinite(aspect) || width <= 0 || height <= 0) return box;

  if (width / height < aspect) {
    const grow = (height * aspect - width) / 2;
    return boundsOps.bounds(box.minX - grow, box.minY, box.maxX + grow, box.maxY);
  }

  const grow = (width / aspect - height) / 2;
  return boundsOps.bounds(box.minX, box.minY - grow, box.maxX, box.maxY + grow);
}

/**
 * World → output coordinates for a frame.
 *
 * The y axis flips: the world has y increasing upward, and every output format here — a
 * canvas, an SVG viewport, a PDF page as addressed by this writer — has y increasing
 * downward. Doing it once, here, is what keeps three exporters from each getting it wrong
 * in a different way.
 */
export function projectorFor(frame: ExportFrame): (point: { x: number; y: number }) => {
  x: number;
  y: number;
} {
  return (point) => ({
    x: (point.x - frame.world.minX) * frame.scale,
    y: (frame.world.maxY - point.y) * frame.scale,
  });
}
